import type { FilesystemBrowseEntry, ProjectEntry } from "@afar-research/contracts";
import { isWindowsAbsolutePath } from "@afar-research/shared/path";

import {
  ensureBrowseDirectoryPath,
  isFilesystemBrowseQuery,
  normalizeProjectPathForComparison,
} from "~/lib/projectPaths";

/** One row of the Environment files picker, named by its absolute path on the Environment host. */
export interface EnvironmentFileRow {
  readonly path: string;
  readonly name: string;
  readonly kind: ProjectEntry["kind"];
  /** What the row reads as: its name in a listing, its checkout-relative path in a search. */
  readonly label: string;
}

/** More rows than one listing draws; a larger folder is narrowed by typing its path. */
export const ENVIRONMENT_FILES_MAX_ROWS = 500;

function hostSeparator(workspaceRoot: string): "/" | "\\" {
  return isWindowsAbsolutePath(workspaceRoot) ? "\\" : "/";
}

function isHidden(row: EnvironmentFileRow): boolean {
  return row.name.startsWith(".");
}

/**
 * One directory's children on the Environment, folders first. Hidden entries
 * follow the visible ones of their kind, so a home folder full of dotfiles
 * still opens on the folders a person means.
 */
export function environmentBrowseRows(
  entries: ReadonlyArray<FilesystemBrowseEntry>,
): ReadonlyArray<EnvironmentFileRow> {
  const rows = entries.map(
    (entry): EnvironmentFileRow => ({
      path: entry.fullPath,
      name: entry.name,
      // An Environment that predates `includeFiles` lists directories only.
      kind: entry.kind ?? "directory",
      label: entry.name,
    }),
  );
  return rows.toSorted((left, right) => {
    if (left.kind !== right.kind) return left.kind === "directory" ? -1 : 1;
    if (isHidden(left) !== isHidden(right)) return isHidden(left) ? 1 : -1;
    return left.name.localeCompare(right.name);
  });
}

/** Checkout search hits, which name paths relative to the thread checkout. */
export function environmentSearchRows(
  workspaceRoot: string,
  entries: ReadonlyArray<ProjectEntry>,
): ReadonlyArray<EnvironmentFileRow> {
  const separator = hostSeparator(workspaceRoot);
  const root = workspaceRoot.replace(/[\\/]+$/, "");
  return entries.map((entry) => {
    const segments = entry.path.split("/").filter((segment) => segment.length > 0);
    return {
      path: [root, ...segments].join(separator),
      name: segments.at(-1) ?? entry.path,
      kind: entry.kind,
      label: entry.path,
    };
  });
}

/**
 * Whether the dialog's search box holds a path to browse rather than words to
 * search the checkout for. Relative paths resolve against the thread checkout.
 */
export function isEnvironmentPathQuery(query: string, workspaceRoot: string): boolean {
  return isFilesystemBrowseQuery(query, isWindowsAbsolutePath(workspaceRoot) ? "windows" : "");
}

/** The directory the dialog opens on: the thread checkout, browsable as a folder. */
export function environmentStartDirectory(workspaceRoot: string): string {
  return ensureBrowseDirectoryPath(workspaceRoot);
}

/** Where the dialog is, named for a person: the checkout by role, anywhere else by path. */
export function environmentLocationLabel(directory: string, workspaceRoot: string): string {
  return normalizeProjectPathForComparison(directory) ===
    normalizeProjectPathForComparison(workspaceRoot)
    ? "Thread checkout"
    : directory;
}
